import { computed, signal } from '@preact/signals';
import { customer } from '@/state/session.js';
import {
  initialCustomerForm,
  validateCustomerForm,
  type AddressFormState,
  type CustomerFormState,
} from '@/lib/customer-form.js';

export const customerForm = signal<CustomerFormState>(initialCustomerForm(null));

/** An error stays hidden until its own field is touched, so a pristine form isn't a wall of red. */
const touched = signal<Partial<Record<keyof CustomerFormState, true>>>({});

/** Set when the customer tries to advance, revealing errors on fields they never reached. */
export const showCustomerErrors = signal(false);

export const customerFormValidation = computed(() => validateCustomerForm(customerForm.value));

export function updateCustomerForm<K extends keyof CustomerFormState>(
  key: K,
  value: CustomerFormState[K],
): void {
  customerForm.value = { ...customerForm.value, [key]: value };
  touched.value = { ...touched.value, [key]: true };
}

/** Deliberately leaves the fields untouched — prefilled is not answered, and a pristine field
 *  shows no error. */
export function seedCustomerAddress(address: AddressFormState): void {
  customerForm.value = { ...customerForm.value, ...address };
}

/** The error to display for a field, or undefined while it should stay hidden. */
export function customerFieldError(key: keyof CustomerFormState): string | undefined {
  if (!showCustomerErrors.value && touched.value[key] !== true) return undefined;
  return customerFormValidation.value.errors[key];
}

/** Seeds from `customer`, so call it after `resetSession`, which nulls that. */
export function resetCustomerForm(): void {
  customerForm.value = initialCustomerForm(customer.value);
  touched.value = {};
  showCustomerErrors.value = false;
}
